import React from "react";
import defaultStripLogo from "../../Header/Images/RMB-Strip-Jaipur.png";

/**
 * RMB Jaipur – Regular Meeting Invitation Poster (3:4 ratio / 1080 x 1440 grid)
 *
 * Built on the exact 1080 x 1440 grid. SVG-native vector layout ensures 100%
 * compatibility with html2canvas and pixel-perfect responsive display.
 */

const NAVY = "#0f1b45";
const TITLE = "#12295a";
const CYAN = "#5ce1e6";
const GOLD_BOX = "#e0b23f";
const CARD = "#1f4b8c";
const GOLD_TXT = "#f3b52f";

const antonFont = "'Anton', 'Arial Narrow', sans-serif";
const fjallaFont = "'Fjalla One', 'Arial Narrow', sans-serif";
const sansFont = "'Montserrat', system-ui, -apple-system, sans-serif";

/* Gold tile icons ---------------------------------------------------- */
const Tile = ({ x, y, w = 84, h = 84, children }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect x="3" y="4" width={w} height={h} rx="12" fill="#00000030" />
    <rect width={w} height={h} rx="12" fill="url(#tileGrad)" stroke="#fbe29a" strokeWidth="2" />
    {children}
  </g>
);

const CalendarIcon = () => (
  <>
    <rect x="16" y="20" width="52" height="48" rx="7" fill="#4a3a14" />
    <rect x="16" y="20" width="52" height="13" rx="6" fill="#2f2408" />
    <rect x="25" y="12" width="6" height="14" rx="3" fill="#2f2408" />
    <rect x="53" y="12" width="6" height="14" rx="3" fill="#2f2408" />
    {[0, 1, 2].flatMap((r) =>
      [0, 1, 2, 3].map((c) => (
        <rect key={`${r}${c}`} x={22 + c * 11} y={38 + r * 9} width="7" height="6" rx="1.5" fill="#f1cf7d" />
      ))
    )}
  </>
);

const ClockIcon = () => (
  <>
    <circle cx="42" cy="42" r="28" fill="#f8dc8c" stroke="#3a2c0c" strokeWidth="4" />
    <path d="M42 24v19l13 8" stroke="#3a2c0c" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="42" cy="42" r="3" fill="#3a2c0c" />
  </>
);

const PinIcon = () => (
  <>
    <path d="M14 66l28-12 28 12-28 12z" fill="none" stroke="#3a2c0c" strokeWidth="4" strokeLinejoin="round" />
    <path d="M42 56C30 44 26 38 26 31a16 16 0 0 1 32 0c0 7-4 13-16 25z" fill="#3a2c0c" />
    <circle cx="42" cy="31" r="6" fill="#f8dc8c" />
  </>
);

/* Fallback vector QR ------------------------------------------------- */
const FakeQr = () => {
  const n = 25;
  const cells = [];
  let s = 7;
  const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
  const finder = (r, c) =>
    (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (finder(r, c)) continue;
      if (rnd() > 0.5) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#111" />);
    }
  }
  const Finder = ({ x, y }) => (
    <g transform={`translate(${x} ${y})`}>
      <rect width="7" height="7" fill="#111" />
      <rect x="1" y="1" width="5" height="5" fill="#fff" />
      <rect x="2" y="2" width="3" height="3" fill="#111" />
    </g>
  );
  return (
    <g>
      {cells}
      <Finder x={0} y={0} />
      <Finder x={n - 7} y={0} />
      <Finder x={0} y={n - 7} />
    </g>
  );
};

/* SVG Text helper with optional textLength fitting ------------------- */
const SvgText = ({
  x,
  cy,
  w,
  size,
  font = sansFont,
  color = TITLE,
  weight = "normal",
  textAnchor = "start",
  letterSpacing,
  children,
  fit = false,
}) => {
  if (!children && children !== 0) return null;
  return (
    <text
      x={x}
      y={cy}
      dominantBaseline="central"
      textAnchor={textAnchor}
      fill={color}
      fontFamily={font}
      fontSize={size}
      fontWeight={weight}
      letterSpacing={letterSpacing}
      {...(fit && w ? { textLength: w, lengthAdjust: "spacingAndGlyphs" } : {})}
    >
      {children}
    </text>
  );
};

const RmbMeetingPoster = React.forwardRef((props, ref) => {
  const {
    logo = defaultStripLogo,
    qr = "",
    qrCaption = "merchant1334199.augp@aubank",
    title = ["RMB JAIPUR'S 127th", "REGULAR MEETING"],
    date = { day: "26th SEPT.", year: "2026", weekday: "SATURDAY" },
    time = ["8:00 AM", "onwards"],
    venue = ["The Fern Jaipur, Series by", "Marriott, Tonk Road"],
    slots = [
      { heading: ["BUSINESS", "PRESENTATION"], name: "RMBIAN: VAIBHAV BAGLA", company: "Natural Consume Care", photo: "" },
      { heading: ["EDUCATION", "Slot"], name: "RMBIAN: KSHITIJ PRASHAD", company: "AAKS Global Associates", photo: "" },
    ],
    dressCode = "Formal",
    charges = "REGISTRATION CHARGES RS. 1,200/- FOR VISITORS",
    note = "( Meeting followed by Breakfast )",
    rsvp = [
      "+91 9829055889  RMBian: Siddharth Makharia",
      "+91 9024111753  RMBian: Gaurav Mathur",
      "+91 7301020313  RMBian: Ashish Bhardwaj",
      "+91 9557969530  RMBian: Dr. Megha Sharma",
    ],
    website = "https://rmbjaipur.com/",
    className = "",
    style = {},
  } = props;

  // Title lines normalization
  const titleLines = Array.isArray(title)
    ? title
    : String(title || "RMB JAIPUR'S 127th\nREGULAR MEETING").split("\n");
  const title0 = titleLines[0] || "RMB JAIPUR'S 127th";
  const title1 = titleLines[1] || "REGULAR MEETING";

  // Date normalization
  let dDay = "26th SEPT.";
  let dYear = "2026";
  let dWeekday = "SATURDAY";
  if (date && typeof date === "object" && date.day) {
    dDay = date.day;
    dYear = date.year || "2026";
    dWeekday = date.weekday || "SATURDAY";
  } else if (typeof date === "string" && date) {
    try {
      const parsed = new Date(date.includes(" ") && !date.includes("T") ? date.replace(" ", "T") : date);
      if (!isNaN(parsed.getTime())) {
        const day = parsed.getDate();
        const s = ["th", "st", "nd", "rd"];
        const v = day % 100;
        const ord = day + (s[(v - 20) % 10] || s[v] || s[0]);
        const months = ["JAN.", "FEB.", "MAR.", "APR.", "MAY", "JUNE", "JULY", "AUG.", "SEPT.", "OCT.", "NOV.", "DEC."];
        const days = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
        dDay = `${ord} ${months[parsed.getMonth()]}`;
        dYear = String(parsed.getFullYear());
        dWeekday = days[parsed.getDay()];
      }
    } catch {}
  }

  // Time lines normalization
  const timeLines = Array.isArray(time)
    ? time
    : String(time || "8:00 AM\nonwards").split("\n");
  const time0 = timeLines[0] || "8:00 AM";
  const time1 = timeLines[1] || "onwards";

  // Venue lines normalization
  let venue0 = "The Fern Jaipur, Series by";
  let venue1 = "Marriott, Tonk Road";
  if (Array.isArray(venue) && venue.length > 0) {
    venue0 = venue[0] || "";
    venue1 = venue[1] || "";
  } else if (typeof venue === "string" && venue) {
    const parts = venue.split(/,|\n/);
    venue0 = parts[0]?.trim() || venue;
    venue1 = parts.slice(1).join(", ").trim() || "";
  }

  // Slots normalization
  const s0 = slots?.[0] || { heading: ["BUSINESS", "PRESENTATION"], name: "", company: "", photo: "" };
  const s1 = slots?.[1] || { heading: ["EDUCATION", "Slot"], name: "", company: "", photo: "" };

  const s0Head = Array.isArray(s0.heading) ? s0.heading : [s0.heading || "BUSINESS", "PRESENTATION"];
  const s1Head = Array.isArray(s1.heading) ? s1.heading : [s1.heading || "EDUCATION", "Slot"];

  return (
    <div
      ref={ref}
      className={`rmb-meeting-poster relative overflow-hidden bg-white shadow-2xl rounded-2xl select-none ${className}`}
      style={{
        width: "100%",
        maxWidth: "560px",
        aspectRatio: "1080 / 1440",
        margin: "0 auto",
        ...style,
      }}
    >
      <svg
        viewBox="0 0 1080 1440"
        className="w-full h-full block"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <defs>
          <linearGradient id="tileGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#f6cf55" />
            <stop offset="1" stopColor="#c8932a" />
          </linearGradient>
          <linearGradient id="bandGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#d9d94a" stopOpacity="0" />
            <stop offset="0.18" stopColor="#dcd94a" stopOpacity="0.95" />
            <stop offset="0.6" stopColor="#c9df6c" stopOpacity="0.9" />
            <stop offset="1" stopColor="#c9df6c" stopOpacity="0" />
          </linearGradient>
          <filter id="softBlur" x="-10%" y="-60%" width="120%" height="220%">
            <feGaussianBlur stdDeviation="4" />
          </filter>

          {/* Photo clip paths */}
          <clipPath id="circlePhoto0">
            <circle cx="680" cy="638" r="83.5" />
          </clipPath>
          <clipPath id="circlePhoto1">
            <circle cx="680" cy="898" r="83.5" />
          </clipPath>
        </defs>

        {/* ───────── LAYER 1: Navy background bar, Cyan lines ───────── */}
        <rect x="704" y="0" width="277" height="1440" fill={NAVY} />

        {/* Cyan horizontal line clusters */}
        {[
          [718, 965, 178], [780, 965, 216], [842, 965, 253],
          [712, 835, 1185], [712, 897, 1222], [712, 960, 1259],
        ].map(([a, b, y]) => (
          <rect key={y} x={a} y={y - 3} width={b - a} height="6" fill="#4cc9ee" />
        ))}

        {/* Ribbon folds */}
        <path d="M982 364 C1020 352 1052 372 1054 408 L1050 434 L982 434 Z" fill="#2fb0bf" />
        <path d="M982 1076 L1050 1076 L1054 1106 C1052 1128 1020 1124 982 1118 Z" fill="#33b6c4" />
        {/* Ribbon body */}
        <path d="M648 430 L1050 430 L1050 1077 L642 1077 L470 748 Q452 718 470 690 Z" fill={CYAN} />

        {/* Yellow heading glow bands */}
        <g filter="url(#softBlur)">
          <rect x="640" y="446" width="398" height="48" rx="10" fill="url(#bandGrad)" />
          <rect x="655" y="506" width="395" height="52" rx="10" fill="url(#bandGrad)" />
          <rect x="645" y="712" width="395" height="54" rx="10" fill="url(#bandGrad)" />
          <rect x="660" y="773" width="385" height="52" rx="10" fill="url(#bandGrad)" />
        </g>

        {/* Left gold tiles with icons */}
        <Tile x={68} y={520}><CalendarIcon /></Tile>
        <Tile x={68} y={690}><ClockIcon /></Tile>
        <Tile x={65} y={853} w={88} h={90}><g transform="translate(2 3)"><PinIcon /></g></Tile>

        {/* Date underline + divider */}
        <rect x="165" y="566" width="285" height="3" fill={GOLD_TXT} />
        <rect x="270" y="572" width="3" height="32" fill={GOLD_TXT} />

        {/* ───────── LAYER 2: Cards & Photo Circles ───────── */}
        {/* Presentation slot 0 card */}
        <rect x="778" y="571" width="272" height="128" rx="24" fill={CARD} />
        {/* Presentation slot 1 card */}
        <rect x="778" y="849" width="272" height="128" rx="24" fill={CARD} />

        {/* Slot 0 Circle Photo */}
        <circle cx="680" cy="638" r="83.5" fill="#cbd5e1" stroke="#dfe6ee" strokeWidth="6" />
        {s0.photo ? (
          <image
            href={s0.photo}
            x="596.5"
            y="554.5"
            width="167"
            height="167"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#circlePhoto0)"
          />
        ) : (
          <g transform="translate(642, 600) scale(3.2)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="680" cy="638" r="83.5" fill="none" stroke="#dfe6ee" strokeWidth="6" />

        {/* Slot 1 Circle Photo */}
        <circle cx="680" cy="898" r="83.5" fill="#cbd5e1" stroke="#dfe6ee" strokeWidth="6" />
        {s1.photo ? (
          <image
            href={s1.photo}
            x="596.5"
            y="814.5"
            width="167"
            height="167"
            preserveAspectRatio="xMidYMid slice"
            clipPath="url(#circlePhoto1)"
          />
        ) : (
          <g transform="translate(642, 860) scale(3.2)">
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
              fill="#64748b"
            />
          </g>
        )}
        <circle cx="680" cy="898" r="83.5" fill="none" stroke="#dfe6ee" strokeWidth="6" />

        {/* Dress code folded tag */}
        <g transform="rotate(-7 805 1020)">
          <path d="M872 985 L910 983 L910 1006" fill="none" stroke={NAVY} strokeWidth="2.5" />
          <polygon points="708,1000 903,1000 903,1042 708,1042" fill={NAVY} />
          <polygon points="708,1042 730,1042 730,1072" fill="#0a1233" />
          <text x="717" y="1025" dominantBaseline="central" fontFamily={sansFont} fontSize="21" fontWeight="bold">
            <tspan fill={GOLD_TXT}>Dress Code: </tspan>
            <tspan fill="#ffffff" fontWeight="600">{dressCode}</tspan>
          </text>
        </g>

        {/* ───────── LAYER 3: Rotary / RMB Jaipur Logo ───────── */}
        {logo ? (
          <image
            href={logo}
            x="50"
            y="65"
            width="605"
            height="120"
            preserveAspectRatio="xMinYMid meet"
          />
        ) : (
          <text x="50" y="125" dominantBaseline="central" fontFamily={sansFont} fontSize="34" fontWeight="bold" fill={TITLE}>
            ROTARY | RMB JAIPUR
          </text>
        )}

        {/* ───────── Left Section Headings & Text ───────── */}
        <SvgText x={65} cy={278} w={533} size={70} font={antonFont} color={TITLE} fit>
          {title0}
        </SvgText>
        <SvgText x={65} cy={361} w={465} size={70} font={antonFont} color={TITLE} fit>
          {title1}
        </SvgText>

        {/* Date Texts */}
        <SvgText x={181} cy={528} w={307} size={76} font={antonFont} color={TITLE} fit>
          {dDay}
        </SvgText>
        <SvgText x={182} cy={587} w={78} size={33} font={sansFont} color={TITLE} weight="600" fit>
          {dYear}
        </SvgText>
        <SvgText x={280} cy={587} w={180} size={33} font={sansFont} color={TITLE} weight="600" fit>
          {dWeekday}
        </SvgText>

        {/* Time Texts */}
        <SvgText x={180} cy={710} w={185} size={58} font={fjallaFont} color={TITLE} fit>
          {time0}
        </SvgText>
        <SvgText x={180} cy={762} w={163} size={58} font={fjallaFont} color={TITLE} fit>
          {time1}
        </SvgText>

        {/* Venue Texts */}
        <SvgText x={162} cy={880} w={336} size={36} font={fjallaFont} color={TITLE} fit={Boolean(venue0)}>
          {venue0}
        </SvgText>
        {venue1 && (
          <SvgText x={162} cy={919} w={255} size={36} font={fjallaFont} color={TITLE} fit>
            {venue1}
          </SvgText>
        )}

        {/* ───────── Right Ribbon Texts ───────── */}
        {/* Slot 0 Header & Details */}
        <SvgText x={773} cy={472} w={142} size={40} font={fjallaFont} color={TITLE} fit>
          {s0Head[0]}
        </SvgText>
        <SvgText x={737} cy={532} w={211} size={40} font={fjallaFont} color={TITLE} fit>
          {s0Head[1]}
        </SvgText>
        <SvgText x={787} cy={620} w={243} size={25} font={fjallaFont} color={GOLD_TXT} fit={Boolean(s0.name)}>
          {s0.name || "RMBIAN SPEAKER"}
        </SvgText>
        <SvgText x={817} cy={654} w={189} size={18} font={sansFont} color="#ffffff" weight="600" fit={Boolean(s0.company)}>
          {s0.company || "RMB Jaipur Member"}
        </SvgText>

        {/* Slot 1 Header & Details */}
        <SvgText x={772} cy={737} w={173} size={42} font={fjallaFont} color={TITLE} fit>
          {s1Head[0]}
        </SvgText>
        <SvgText x={825} cy={800} w={68} size={42} font={fjallaFont} color={TITLE} fit>
          {s1Head[1]}
        </SvgText>
        <SvgText x={811} cy={898} w={228} size={25} font={fjallaFont} color={GOLD_TXT} fit={Boolean(s1.name)}>
          {s1.name || "RMBIAN PRESENTER"}
        </SvgText>
        <SvgText x={839} cy={925} w={159} size={15} font={sansFont} color="#ffffff" weight="600" fit={Boolean(s1.company)}>
          {s1.company || "RMB Jaipur Member"}
        </SvgText>

        {/* ───────── Registration Gold Box ───────── */}
        <rect x={26} y={1029} width={591} height={181} rx={24} fill={GOLD_BOX} />
        <SvgText x={51} cy={1068} w={530} size={21.5} font={sansFont} color={TITLE} weight="bold" fit>
          {charges}
        </SvgText>
        <SvgText x={115} cy={1140} w={363} size={23} font={sansFont} color="#1d3a6e" weight="600" fit>
          {note}
        </SvgText>

        {/* Payment QR Box */}
        <g transform="translate(495, 1075)">
          <rect x="0" y="0" width="105" height="115" rx="8" fill="#ffffff" />
          {qr ? (
            <image href={qr} x="8" y="8" width="89" height="89" preserveAspectRatio="xMidYMid meet" />
          ) : (
            <g transform="translate(10, 8) scale(3.4)">
              <FakeQr />
            </g>
          )}
          <text
            x="52.5"
            y="108"
            textAnchor="middle"
            fontFamily={sansFont}
            fontSize="5.5"
            fontWeight="bold"
            fill="#111"
          >
            {qrCaption}
          </text>
        </g>

        {/* ───────── RSVP Section ───────── */}
        <text
          x={43}
          y={1297}
          dominantBaseline="central"
          textAnchor="middle"
          transform="rotate(-90 43 1297)"
          fontFamily={sansFont}
          fontSize="31"
          fontWeight="bold"
          fill={TITLE}
          letterSpacing="0.04em"
        >
          RSVP
        </text>

        {rsvp.map((line, i) => (
          <SvgText
            key={i}
            x={71}
            cy={1249 + i * 32.7}
            w={[574, 491, 516, 556][i] ?? 540}
            size={28}
            font={sansFont}
            color="#1a2748"
            weight="500"
            fit
          >
            {line}
          </SvgText>
        ))}

        {/* ───────── Website Gold Bar ───────── */}
        <rect x={26} y={1376} width={591} height={39} rx={8} fill={GOLD_BOX} />
        <SvgText x={321.5} cy={1395} size={25} font={sansFont} color={TITLE} weight="bold" textAnchor="middle">
          {website}
        </SvgText>
      </svg>
    </div>
  );
});

export default RmbMeetingPoster;
